import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import type { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community';
import cx from 'classnames';

const customIcons = {
  sortAscending:
    '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" height="0.8em" width="0.8em" xmlns="http://www.w3.org/2000/svg"><path d="M858.9 689L530.5 308.2c-9.4-10.9-27.5-10.9-37 0L165.1 689c-12.2 14.2-1.2 35 18.5 35h656.8c19.7 0 30.7-20.8 18.5-35z"></path></svg>',
  sortDescending:
    '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" height="0.8em" width="0.8em" xmlns="http://www.w3.org/2000/svg"><path d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z"></path></svg>',
  sortUnSort:
    '<div className="flex p-0"><svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" height="0.6em" width="0.7em" xmlns="http://www.w3.org/2000/svg"><path d="M858.9 689L530.5 308.2c-9.4-10.9-27.5-10.9-37 0L165.1 689c-12.2 14.2-1.2 35 18.5 35h656.8c19.7 0 30.7-20.8 18.5-35z"></path></svg><svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" height="0.6em" width="0.7em" xmlns="http://www.w3.org/2000/svg"><path d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z"></path></svg></div>',
};

export * from 'ag-grid-community';
export * from 'ag-grid-enterprise';
export * from 'ag-grid-react';

export type ColumnDefs = ColDef | ColGroupDef;

type SelectionType = { selectAll: boolean; toggledNodes: any[] };

export interface TableContainerProps extends Omit<AgGridReactProps, 'ref'> {
  onSelectChanged?: (selection: SelectionType) => void;
  columnDefs: Array<ColumnDefs>;
  selectedRows?: any[];
  wrapperClassName?: string;
}

const defaultCellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const initialDefaultColDef: ColumnDefs = {
  lockPinned: true,
  cellStyle: defaultCellStyle,
  suppressMenu: true,
};

const defaultGridOptions: GridOptions = {
  fullWidthCellRenderer: true,
  suppressClickEdit: true,
  colResizeDefault: 'shift',
  suppressScrollOnNewData: true,
  suppressRowTransform: true,
  animateRows: true,
  enableRangeSelection: true,
  suppressRowClickSelection: true,
  rowSelection: 'multiple',
  groupRowsSticky: true,
  icons: customIcons,
};

export const Table = React.forwardRef<AgGridReact, TableContainerProps>(
  (
    props: TableContainerProps,
    ref: MutableRefObject<AgGridReact>
  ): React.ReactElement => {
    const innerRef = useRef<AgGridReact>(null);

    useImperativeHandle(ref, () => innerRef.current);

    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const handleOnSelectionChanged = useCallback(() => {
      if (props?.onSelectChanged) {
        let selection: any;
        if (props.rowModelType === 'serverSide')
          selection = innerRef?.current?.api?.getServerSideSelectionState();
        else {
          const toggledNodes = innerRef?.current?.api?.getSelectedNodes();
          const selectAll = toggledNodes.length === props.rowData.length;
          selection = {
            selectAll,
            toggledNodes: selectAll ? [] : toggledNodes,
          };
        }
        props?.onSelectChanged(selection);
      }
    }, [props.rowModelType]);

    const onFirstDataRendered = useCallback(() => {
      setTimeout(() => innerRef?.current?.api?.sizeColumnsToFit(), 1);

      if (props?.selectedRows) {
        innerRef?.current?.api.forEachNode((node) => {
          node.setSelected(
            !!props?.selectedRows.find((row) => row.id === node.data.id)
          );
        });
      }
    }, [props?.selectedRows]);

    const onSortChanged = useCallback(() => {
      innerRef?.current?.api?.redrawRows();
    }, []);

    useEffect(() => {
      if (tableWrapperRef.current) {
        const resizeObserver = new ResizeObserver(() => {
          innerRef?.current?.api?.sizeColumnsToFit();
        });

        resizeObserver.observe(tableWrapperRef.current);

        return () => resizeObserver.disconnect();
      }
    }, [tableWrapperRef]);

    return (
      <div
        className={cx(
          'w-full h-full overflow-hidden',
          props.wrapperClassName && props.wrapperClassName
        )}
      >
        <div className="ag-theme-alpine w-full h-full" ref={tableWrapperRef}>
          <AgGridReact
            defaultColDef={{ ...initialDefaultColDef, ...props?.defaultColDef }}
            onSelectionChanged={handleOnSelectionChanged}
            onFirstDataRendered={onFirstDataRendered}
            onSortChanged={onSortChanged}
            {...defaultGridOptions}
            {...props}
            ref={innerRef}
          />
        </div>
      </div>
    );
  }
);

Table.displayName = 'Table';

export default Table;
